import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../AuthContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const FeaturedSpeakers = () => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const isArabic = i18n.language === "ar";
  const [speakers, setSpeakers] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const getFeaturedSpeakers = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          "/api/v1/users/speakers/home?type=3"
        );

        if (response.data && response.data.items) {
          setSpeakers(response.data.items);
        } else {
          setError(t("noResults"));
        }
      } catch (error) {
        setError(t("fetchError"));
        console.error("Error fetching featured speakers:", error);
      } finally {
        setLoading(false);
      }
    };

    getFeaturedSpeakers();
  }, [t]);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      const fetchFavorites = async () => {
        try {
          const response = await axiosInstance.get(
            `/api/v1/favorites/speakers/${user.id}`
          );
          setFavorites(response.data.favorites || []);
        } catch (error) {
          console.error("Error fetching favorites:", error);
        }
      };

      fetchFavorites();
    }
  }, [isAuthenticated, user]);

  const toggleFavorite = async (speakerId) => {
    if (isAuthenticated) {
      try {
        const isFavorite = favorites.includes(speakerId);
        const action = isFavorite ? "remove" : "add";

        // إرسال الطلب بناءً على ما إذا كان المفضل موجودًا أم لا
        if (action === "add") {
          await axiosInstance.post(`/api/v1/favorites/speakers/${speakerId}`);
          toast.success(t("favoriteAdded")); // تظهر رسالة عند الإضافة بنجاح
        } else {
          await axiosInstance.delete(`/api/v1/favorites/speakers/${speakerId}`);
          toast.success(t("favoriteRemoved")); // تظهر رسالة عند الحذف بنجاح
        }

        // تحديث قائمة المفضلات بعد الإضافة أو الحذف
        setFavorites((prevFavorites) => {
          if (isFavorite) {
            return prevFavorites.filter((id) => id !== speakerId);
          }
          return [...prevFavorites, speakerId];
        });
      } catch (error) {
        console.error("Error updating favorite:", error);
        toast.error(t("errorUpdatingFavorite"));
      }
    } else {
      toast.warn(t("notAuthenticated"));
    }
  };

  return (
    <section className="py-5">
      <div className="container py-4">
        <div className="row justify-content-center mb-5">
          <div className="col-md-8 text-center">
            <h2 className="fw-bold text-primary-custom mb-3">
              {t("featuredSpeakers")}
            </h2>
            <p className="text-muted">{t("featuredSpeakersSubtitle")}</p>
          </div>
        </div>

        <div className="row g-4">
          {loading && <p>{t("loading")}</p>}
          {error && <p className="text-danger">{error}</p>}

          {!loading &&
            !error &&
            speakers.map((speaker, index) => (
              <div className="col-md-4" key={index}>
                <div className="card speaker-card shadow-sm h-100 position-relative">
                  {isAuthenticated && (
                    <button
                      className={`favorite-btn ${
                        favorites.includes(speaker.id) ? "active" : ""
                      }`}
                      aria-label="إضافة إلى المفضلة"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleFavorite(speaker.id);
                      }}
                    >
                      <i className="fas fa-star"></i>
                    </button>
                  )}

                  <a
                    href={`/speaker-detail/${speaker.id}`}
                    className="speaker-card-link text-decoration-none text-dark d-block h-100"
                  >
                    <div className="speaker-img-container">
                      <img
                        src={speaker.profileImage}
                        className="speaker-img"
                        alt={speaker.englishFullName || speaker.arabicFullName}
                        style={{
                          width: "100%", 
                          height: "auto", 
                          objectFit: "cover", 
                        }}
                      />

                      <div className="speaker-bio-overlay">
                        <div className="speaker-bio-content">
                          <p>
                            {speaker.englishShortBiography ||
                              speaker.arabicShortBiography ||
                              t("noBio")}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      <h5 className="card-title fw-bold">
                        {speaker.englishFullName || speaker.arabicFullName}
                      </h5>
                      {/* <p className="card-text text-muted">
                        {speaker.englishJobTitle ||
                          speaker.arabicJobTitle ||
                          t("noJobTitle")}
                      </p> */}
                      <div className="speaker-location">
                        <i className="fas fa-map-marker-alt me-1"></i>
                        <span>{speaker.city?.name || t("notAvailable")}</span>
                      </div>
                      <div className="mt-2">
                        {speaker.speakerSpecialies?.map((speciality, index) => (
                          <span key={index} className="topic-badge">
                            {isArabic
                              ? speciality.speciality.arabicName
                              : speciality.speciality.englishName}
                          </span>
                        ))}
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            ))}
        </div>

        <div className="text-center mt-5">
          <a href="/speakers" className="btn btn-outline-primary px-4 py-2">
            {t("viewAllSpeakers")}
          </a>
        </div>
      </div>

      <ToastContainer />
    </section>
  );
};

export default FeaturedSpeakers;
